<template>
  <div class="rb-table-card" :class="{ 'rb-table-card--embedded': injectedReportData }">
    <!-- Табличный вид не рендерит график, поэтому тулбар (тип отчёта + период) живёт здесь.
         В дашборд-виджете (hideControls) название и так есть в шапке виджета — весь ряд лишний. -->
    <div v-if="!hideControls" class="d-flex justify-content-between align-items-center mb-3">
      <div class="rb-sql-table-title">
        {{ $t('ReportBuilder.sqlTab.resultTitle') }}
        <span v-if="rows.length" class="rb-sql-table-count">
          {{ $t('ReportBuilder.sqlTab.rowsCount', { count: rows.length }) }}
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <ControlChart />
        <CompareControl v-if="!injectedReportData" />
        <DateRange compact />
      </div>
    </div>

    <el-alert
        v-if="errors.length"
        type="error"
        :title="$t('ReportBuilder.sqlTab.errorsTitle')"
        :closable="false"
        show-icon
        class="mb-3"
    >
      <div v-for="(error, idx) in errors" :key="idx" class="rb-sql-error-line">{{ error }}</div>
    </el-alert>

    <div v-if="rows.length && (hasCategories || metricColumns.length)" class="rb-sql-table-hint mb-2">
      {{ $t('ReportBuilder.sqlTab.stylesHint') }}
    </div>

    <el-table
        v-if="rows.length"
        :data="pageRows"
        border
        stripe
        max-height="500"
        size="small"
    >
      <el-table-column
          v-for="column in columns"
          :key="column.name"
          :prop="column.name"
          :label="column.name"
          min-width="140"
          :show-overflow-tooltip="!compareActive"
      >
        <!-- Заголовок колонки-метрики — цвет серии и её подпись -->
        <template #header>
          <SeriesColorPicker
              v-if="metricIndex(column.name) !== -1"
              color-only
              :show-color="!hasCategories"
              :color="metricColor(metricIndex(column.name))"
              :name="metricLabel(metricIndex(column.name))"
              :name-placeholder="column.name"
              :palette="palette"
              @update="patch => updateMetric(metricIndex(column.name), patch)"
          >
            <template #reference>
              <span class="rb-col-head">
                <span
                    v-if="!hasCategories"
                    class="rb-dot"
                    :style="{ backgroundColor: metricColor(metricIndex(column.name)) }"
                ></span>
                <span>{{ metricLabel(metricIndex(column.name)) || column.name }}</span>
                <i class="fas fa-pen rb-col-pen"></i>
              </span>
            </template>
          </SeriesColorPicker>
          <span v-else>{{ column.name }}</span>
        </template>

        <template #default="scope">
          <!-- Ячейка категории — цвет сектора/линии и своя подпись значения -->
          <div v-if="column.name === categoryColumn && rowSeparators(scope.row)" class="rb-category-cell">
            <SeriesColorPicker
                color-only
                resettable
                small
                :color="categoryColor(rowSeparators(scope.row))"
                :name="customLabel(rowSeparators(scope.row))"
                :name-placeholder="rawValue(scope.row)"
                :palette="palette"
                @update="patch => updateCategory(rowSeparators(scope.row), patch)"
            />
            <span class="rb-category-label">{{ displayLabel(scope.row) }}</span>
            <span v-if="isRenamed(rowSeparators(scope.row))" class="rb-category-raw">{{ rawValue(scope.row) }}</span>
          </div>
          <template v-else>
            <div class="rb-sql-value">{{ formatCell(scope.row[column.name]) }}</div>
            <!-- Сравнение с другим периодом: дельта только у числовых колонок -->
            <div
                v-if="cellDelta(scope.row, column)"
                class="rb-sql-delta"
                :class="'rb-sql-delta--' + cellDelta(scope.row, column).dir"
                :title="$t('ReportBuilder.compare.kpiHint', {
                  value: formatCell(cellDelta(scope.row, column).prev),
                  period: comparePeriodShortLabel
                })"
            >
              {{ cellDelta(scope.row, column).text }}
            </div>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <div v-else-if="!errors.length" class="rb-empty-state">
      <i class="fas fa-table rb-empty-icon"></i>
      <span class="rb-empty-text">{{ $t('ReportBuilder.sqlTab.noResult') }}</span>
    </div>

    <div v-if="rows.length > pageSize" class="d-flex justify-content-end mt-3">
      <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          small
          background
          layout="total, prev, pager, next"
          :total="rows.length"
          @current-change="currentPage = $event"
      />
    </div>
  </div>
</template>

<script>
import DateRange from "@/views/report_builder/builder/_dateRange.vue"
import ControlChart from "@/components/reports/_control_chart.vue"
import CompareControl from "@/components/reports/_compare_control.vue"
import SeriesColorPicker from "@/views/report_builder/builder/_series_color_picker.vue"
import { rowCategory, formatCategoryValue } from "@/helpers/reports/sqlChartAdapter"
import { categoryKey, getCategoryStyle, withCategoryStyle } from "@/helpers/reports/categoryStyles"
import { SERIES_PALETTE } from "@/helpers/reports/palette"
import { normalizeCompare, isCompareSupported, alignSqlCompareRows, formatDeltaPct } from "@/helpers/reports/compare"

const AGGREGATED_TYPES = ['pie', 'bar_horizontal', 'number', 'map']

export default {
  name: "SqlTable",
  components: { DateRange, ControlChart, CompareControl, SeriesColorPicker },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    }
  },
  props: {
    hideControls: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 100,
      palette: SERIES_PALETTE
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    sourceRequest() {
      return this.reportData.source_request || {}
    },
    rows() {
      return this.reportData.sqlRows || []
    },
    columns() {
      return this.reportData.sqlColumns || []
    },
    errors() {
      return this.reportData.sqlErrors || []
    },
    metricColumns() {
      return (this.sourceRequest.requests || []).map(request => request.column).filter(Boolean)
    },
    // Колонка, значения которой стали категориями графика: для круговой/столбцов это
    // ось X, для временных рядов — колонка разбивки
    categoryColumn() {
      const x = this.sourceRequest.x || null
      const separator = (this.sourceRequest.separators || [])[0] || null
      return AGGREGATED_TYPES.includes(this.reportData.type_chart) ? (x || separator) : separator
    },
    hasCategories() {
      return !!this.categoryColumn && !!this.rows.length && !!this.rowSeparators(this.rows[0])
    },
    // Цвет, которым график реально нарисовал каждую категорию: свой либо палитра по индексу
    categoryColors() {
      const map = {}
      const items = this.reportData.chartData || []
      items.forEach((item, i) => {
        const key = categoryKey(item.separators)
        if (!key || map[key]) return
        map[key] = getCategoryStyle(this.reportData.visual, item.separators).color
          || this.palette[i % this.palette.length]
      })
      return map
    },
    pageRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.rows.slice(start, start + this.pageSize)
    },
    // --- Сравнение периодов ---
    compareActive() {
      return !!normalizeCompare(this.reportData.visual)
        && isCompareSupported(this.reportData.type, this.reportData.type_chart)
        && !!(this.reportData.compareSqlRows || []).length
    },
    comparePeriodShortLabel() {
      const mode = normalizeCompare(this.reportData.visual)?.mode
      if (mode === 'previous_year') return this.$t('ReportBuilder.compare.previousYearShort')
      if (mode === 'custom') return this.$t('ReportBuilder.compare.customShort')
      return this.$t('ReportBuilder.compare.previousShort')
    },
    // Числовые колонки, к которым дельта вообще применима: метрики отчёта плюс всё,
    // что бэкенд определил как число. Дата или строка сравнению не подлежат.
    numericColumnNames() {
      const names = new Set(this.metricColumns)
      for (const c of this.columns) {
        if (c.type === 'number' || c.type === 'int' || c.type === 'float') names.add(c.name)
      }
      return names
    },
    // Строка сравнения для каждой строки основного результата, по ссылке на сам объект:
    // el-table отдаёт в слот строку, а не её индекс, а пагинация индекс сдвигает
    compareRowFor() {
      if (!this.compareActive) return null
      const aligned = alignSqlCompareRows(
        this.rows,
        this.reportData.compareSqlRows,
        this.sourceRequest,
        this.columns
      )
      const map = new Map()
      this.rows.forEach((row, i) => map.set(row, aligned[i]))
      return map
    }
  },
  watch: {
    rows() {
      this.currentPage = 1
    }
  },
  methods: {
    // Дельта ячейки к сопоставленной строке сравнительного периода
    cellDelta(row, column) {
      if (!this.compareRowFor || !this.numericColumnNames.has(column.name)) return null
      const cmpRow = this.compareRowFor.get(row)
      if (!cmpRow) return null
      const prev = cmpRow[column.name]
      const current = row[column.name]
      if (prev === undefined || prev === null || current === undefined || current === null) return null
      if (isNaN(Number(prev)) || isNaN(Number(current))) return null
      const text = formatDeltaPct(current, prev)
      if (!text) return null
      return {
        text,
        dir: Number(current) > Number(prev) ? 'up' : (Number(current) < Number(prev) ? 'down' : 'flat'),
        prev
      }
    },
    rowSeparators(row) {
      return rowCategory(row, this.sourceRequest, this.reportData.type_chart)
    },
    rawValue(row) {
      return formatCategoryValue(row?.[this.categoryColumn])
    },
    // В ячейке показываем значение самой колонки, а не всю склейку многоуровневой
    // категории — своя подпись перекрывает его, если задана
    displayLabel(row) {
      return this.customLabel(this.rowSeparators(row)) || this.rawValue(row)
    },
    customLabel(separators) {
      return getCategoryStyle(this.reportData.visual, separators).label || ''
    },
    isRenamed(separators) {
      return !!getCategoryStyle(this.reportData.visual, separators).label
    },
    categoryColor(separators) {
      const key = categoryKey(separators)
      return this.categoryColors[key] || this.palette[0]
    },
    metricIndex(columnName) {
      return this.metricColumns.indexOf(columnName)
    },
    metricLabel(index) {
      const request = (this.sourceRequest.requests || [])[index]
      return request?.label || ''
    },
    metricColor(index) {
      const request = (this.sourceRequest.requests || [])[index]
      return request?.color || this.palette[index % this.palette.length]
    },
    updateMetric(index, patch) {
      if (index < 0) return
      if (this.injectedReportData) {
        const requests = this.sourceRequest.requests || []
        if (requests[index]) requests[index] = { ...requests[index], ...patch }
        return
      }
      this.$store.commit('ReportFromBuilder/updateRequest', { index, patch })
    },
    updateCategory(separators, patch) {
      if (this.injectedReportData) {
        this.injectedReportData.visual = withCategoryStyle(this.injectedReportData.visual, separators, patch)
        return
      }
      this.$store.commit('ReportFromBuilder/setCategoryStyle', { separators, patch })
    },
    formatCell(cellValue) {
      if (cellValue === null || cellValue === undefined) return ''
      if (typeof cellValue === 'object') return JSON.stringify(cellValue)
      return cellValue
    }
  }
}
</script>

<style scoped>
.rb-table-card {
  background: var(--ui-bg-card, #fff);
  border: 1px solid var(--ui-border, #e8e8e8);
  border-radius: 12px;
  padding: 20px;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
}

/* Embedded in a dashboard widget: the widget's own card already provides the chrome
   (title, border), so drop ours — same treatment as _chart.vue's .main-chart--embedded. */
.rb-table-card--embedded {
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 0;
  margin-top: 0;
  height: 100%;
}

.rb-sql-table-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ui-text-1, #303133);
}

.rb-sql-table-count {
  font-size: 12px;
  font-weight: 400;
  color: var(--ui-text-3, #909399);
  margin-left: 8px;
}

.rb-sql-table-hint {
  font-size: 11px;
  color: var(--ui-text-3, #909399);
}

.rb-sql-error-line {
  font-size: 12px;
  margin-top: 2px;
}

.rb-col-head {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.rb-col-pen {
  font-size: 10px;
  opacity: 0;
  transition: opacity 0.15s;
  color: var(--ui-text-3, #909399);
}

.rb-col-head:hover .rb-col-pen {
  opacity: 1;
}

.rb-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.rb-category-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rb-category-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rb-category-raw {
  font-size: 11px;
  color: var(--ui-text-3, #909399);
  flex-shrink: 0;
}

.rb-empty-state {
  flex: 1 1 0;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--ui-text-4, #c0c4cc);
}

.rb-empty-icon {
  font-size: 22px;
  opacity: 0.5;
}

.rb-empty-text {
  font-size: 12px;
  font-weight: 500;
}

/* Сравнение периодов: дельта под значением ячейки */
.rb-sql-value {
  line-height: 1.2;
}

.rb-sql-delta {
  margin-top: 1px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--ui-text-3, #909399);
  white-space: nowrap;
}

.rb-sql-delta--up {
  color: #1f9d6b;
}

.rb-sql-delta--down {
  color: #d95757;
}
</style>
